/**
 * 경제(상자 구매) 커맨드 (일감 S2a) — GameScene buyGoldBox/buyDiamondBox 추출 (Phaser-free).
 * 도메인 결정 + ShellPorts 부수효과만 — 뷰 갱신은 invalidateUi → TabRefresher 경로.
 * 컨트롤러는 ctx getter 경유 (applySaveData 교체 stale 방지 규약).
 */
import { t } from "../../i18n/i18n.js";
import { formatBig } from "../../ui/formatNumber.js";
import { EconomyController } from "../../systems/EconomyController.js";
import type { GameContext } from "../GameContext.js";
import type { ResolvedReward } from "../../systems/RewardResolver.js";
import type { ModuleInstance } from "../../models/ModuleInstance.js";
import type { ShellPorts } from "./ports.js";
import { RewardService } from "./RewardService.js";
import { trackFirstPurchase } from "../../analytics/trackFirstPurchase.js";

export interface EconomyCommandDeps {
  ctx: GameContext;
  ports: ShellPorts;
  rewards: RewardService;
  discovered: () => ReadonlySet<string>;
  /**
   * 획득 팝업 — 일일상점/던전과 동일 RewardAcquisitionModal (RewardModalPresenter.open).
   * instances = grant가 생성한 모듈 인스턴스(모달이 실제 조각 모양을 그리는 데 사용).
   */
  openRewardModal: (reward: ResolvedReward, instances?: readonly ModuleInstance[]) => void;
  /** 구매 성공 알림 — 가이드퀘스트 actionCompleted 목표 진행. */
  onActionCompleted?: (target: string) => void;
}

export class EconomyCommands {
  constructor(private readonly deps: EconomyCommandDeps) {}

  /**
   * 골드 상자 구매 — 골드 차감 → 새 ModuleInstance 획득 (지시문 §6·§19).
   * 성공 시 일일상점/던전과 동일한 획득 팝업(openRewardModal) 직행 — 하단 토스트는 대체됨.
   * setBoxResult는 실패 분기(골드 부족·추첨 풀 공백)의 에러 토스트 전용이며, 성공 경로에선 빈 문자열로 잔존 에러를 클리어.
   */
  buyGoldBox(): void {
    const { ctx, ports } = this.deps;
    const cost = EconomyController.goldBoxCost(ctx.goldBox().boxOpenCount);
    if (ctx.economy().gold.lt(cost)) {
      ports.setBoxResult(t("toast.goldShort", { cost: formatBig(cost) }));
      ports.invalidateUi();
      return;
    }
    const drop = ctx.goldBox().open([...this.deps.discovered()]);
    if (!drop) {
      // 추첨 풀이 비면(이론상 도달 X) 차감 없이 종료 — 골드 소실 방지.
      ports.setBoxResult(t("toast.noModule"));
      ports.invalidateUi();
      return;
    }
    ctx.economy().trySpend(cost); // 결과 확정 후 차감 (환불 불필요).
    ctx.soundController().play("box");
    // 통합 리워드 경로로 지급 (등급 추첨 모듈) — 추첨/pity/가격은 GoldModuleBox 유지, 등급은 drop.rarity.
    const reward = RewardService.empty({ moduleDrops: [drop] });
    const insts = this.deps.rewards.grant(reward);
    ctx.analytics().log("purchase", { type: "gold_box" });
    trackFirstPurchase(ctx.analytics(), ctx.analyticsMilestones()); // 첫 구매 1회 퍼널 (funnel #9)
    this.deps.openRewardModal(reward, insts); // 획득 팝업 — 하단 토스트 대체.
    this.deps.onActionCompleted?.("shop.goldBox");
    ports.setBoxResult(""); // 이전 에러 토스트(골드 부족 등) 잔존 클리어.
    ports.invalidateUi();
    ports.persist();
  }

  /**
   * 골드 상자 10연 구매 — 골드 차감 → 새 ModuleInstance 10개 획득. 가격은 구매 시점 단가의 ×goldBox10PayUnits.
   * open()을 10회 호출 — 누진 boxOpenCount·등급 천장 카운터가 자연히 누적되어 단발과 동일 경로(천장 공용).
   * 결과는 일일상점/던전과 동일한 다중 셀 획득 그리드(openRewardModal)로 표시.
   */
  buyGoldBox10(): void {
    const { ctx, ports } = this.deps;
    const cost = EconomyController.goldBox10Cost(ctx.goldBox().boxOpenCount);
    if (ctx.economy().gold.lt(cost)) {
      ports.setBoxResult(t("toast.goldShort", { cost: formatBig(cost) }));
      ports.invalidateUi();
      return;
    }
    const discovered = [...this.deps.discovered()];
    const drops = Array.from({ length: 10 }, () => ctx.goldBox().open(discovered)).filter(
      (d): d is NonNullable<typeof d> => d !== null,
    );
    if (drops.length === 0) {
      // 추첨 풀이 비면(이론상 도달 X) 차감 없이 종료 — 골드 소실 방지.
      ports.setBoxResult(t("toast.noModule"));
      ports.invalidateUi();
      return;
    }
    ctx.economy().trySpend(cost); // 결과 확정 후 차감 (환불 불필요).
    ctx.soundController().play("box");
    const reward = RewardService.empty({ moduleDrops: drops });
    const insts = this.deps.rewards.grant(reward);
    ctx.analytics().log("purchase", { type: "gold_box_10" });
    trackFirstPurchase(ctx.analytics(), ctx.analyticsMilestones()); // 첫 구매 1회 퍼널 (funnel #9)
    this.deps.openRewardModal(reward, insts); // 다중 셀 획득 그리드.
    this.deps.onActionCompleted?.("shop.goldBox10");
    ports.setBoxResult(""); // 이전 에러 토스트 잔존 클리어.
    ports.invalidateUi();
    ports.persist();
  }

  /** 다이아 강화 상자 구매 — 다이아 차감 → 강화석 지급 (지시문 §7 방식 A·§19). */
  buyDiamondBox(): void {
    const { ctx, ports } = this.deps;
    const cost = EconomyController.diamondBoxCost();
    if (!ctx.economy().trySpendDiamond(cost)) {
      ports.setBoxResult(t("toast.diamondShort", { cost: formatBig(cost) }));
      ports.invalidateUi();
      return;
    }
    ctx.soundController().play("box");
    const stones = ctx.diamondBox().open();
    ctx.analytics().log("purchase", { type: "diamond_box" });
    trackFirstPurchase(ctx.analytics(), ctx.analyticsMilestones()); // 첫 구매 1회 퍼널 (funnel #9)
    // 통합 리워드 경로로 지급 (강화석) — 추첨/가격은 DiamondUpgradeBox 유지.
    this.deps.rewards.grant(RewardService.empty({ upgradeStone: stones }));
    this.deps.onActionCompleted?.("shop.diamondBox");
    ports.setBoxResult(
      t("toast.stone", { stones, total: ctx.upgradeInv().universalUpgradeStone }),
    );
    ports.invalidateUi();
    ports.persistNow(); // 다이아(유료 재화) 소비 — 즉시 저장(F5/종료 race 방지)
  }

  /** 다음 환생 요구 챕터를 초기 조건으로 되돌리는 다이아 상품. */
  buyRebirthConditionReset(): void {
    const { ctx, ports } = this.deps;
    const cost = EconomyController.rebirthConditionResetCost();
    if (ctx.rebirth().requiredChapter() <= ctx.rebirth().initialRequiredChapter()) {
      ports.setBoxResult(
        t("toast.rebirthConditionAlreadyInitial", {
          chapter: ctx.rebirth().initialRequiredChapter(),
        }),
      );
      ports.invalidateUi();
      return;
    }
    if (!ctx.economy().trySpendDiamond(cost)) {
      ports.setBoxResult(t("toast.diamondShort", { cost: formatBig(cost) }));
      ports.invalidateUi();
      return;
    }
    ctx.rebirth().resetRequiredChapterToInitial();
    ctx.soundController().play("levelup");
    ctx.analytics().log("purchase", { type: "rebirth_condition_reset" });
    trackFirstPurchase(ctx.analytics(), ctx.analyticsMilestones());
    ports.setBoxResult(
      t("toast.rebirthConditionReset", {
        chapter: ctx.rebirth().initialRequiredChapter(),
      }),
    );
    ports.invalidateUi();
    ports.persistNow();
  }
}
